import { forwardRef, memo, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { SendInputIcon } from "components/icons";
import PeopleTagList from "./PeopleTagList";
import SendMessageFile from "./SendMessageFile";
import FullEmojiLayout from "./FullEmojiLayout";
import PastePopper from "./PastePopper";
import { getAllLetterIndex, removeDuplicateInArray } from "utils";
import { StorageUtil } from "utils";
import { useConversationContext } from "../ConversationContext";
import { isGroupOrChannelType } from "utils/view.utils";
import { getCursorPosition } from "hooks/useInputEvent";
import { replaceName2Id } from "utils/message.utils";
import { useDraftMessage } from "hooks";
import { getFilterArray, getWordAtCursor, replaceByMentionName } from "./ChatInput.helper";
import Quote from "./Quote";
import { useDispatch, useSelector } from "react-redux";
import { ConversationActions, ConversationSelectors } from "redux-store";
import { Close } from "@mui/icons-material";
import { createSelector } from "reselect";
import { getNSLang } from "utils/lang.utils";

const memoizedReduxState = createSelector(
  [ConversationSelectors.getSelectedGroupId, ConversationSelectors.getThreadingId],
  (selectedGroupId, selectedThreadId) => {
    return {
      isThreadMode: Boolean(selectedThreadId),
      categoryId: selectedThreadId || selectedGroupId,
    };
  },
);

const MessengerChatInput = ({ placeholder, onSendMessage, isAllowAttach = true, isMediaUpload = false }) => {
  const classes = useStyles();
  const mentionIdListRef = useRef([]);
  const { groupDetail } = useConversationContext();
  const { inputRef, isShowUploadIcon, insertContentInput, updateMessageContent, resetDraftMessage } = useDraftMessage(
    isAllowAttach,
    draftMention => {
      mentionIdListRef.current = draftMention;
    },
  );
  const dispatch = useDispatch();

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  const quoteMessage = useSelector(ConversationSelectors.getQuoteMessage);
  const { isThreadMode, categoryId } = useSelector(memoizedReduxState);
  const sendOpt = useSelector(ConversationSelectors.getSendOpt);

  const isSendAsDirectMsg = sendOpt === SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;

  const [isShowTagList, setIsShowTagList] = useState(false);
  const [filteredGroupMembers, setFilteredGroupMembers] = useState([]);
  const [pasterAnchorEl, setPasterAnchorEl] = useState(null);

  const mentionList = useMemo(() => {
    if (!groupDetail.groupMembers) {
      return [];
    }

    const mentionMemberList = groupDetail.groupMembers.filter(item => item.id !== accountId);

    if (isGroupOrChannelType(groupDetail.groupType)) mentionMemberList.push(SystemConstant.MENTION_ALL);

    return mentionMemberList;
  }, [groupDetail.groupMembers]);

  const handleResetInput = () => {
    mentionIdListRef.current = [];
    updateMessageContent("");

    handleCloseTagList();
    setFilteredGroupMembers(mentionList);
    setPasterAnchorEl(null);
  };

  const handleKeyDown = event => {
    if (event.keyCode === 32) {
      // Space
      handleCloseTagList();
    } else if (event.keyCode === 13) {
      if (event.shiftKey) {
        if (event.type === "keypress" && inputRef.current) {
          // ShiftEnter
          updateMessageContent(inputRef.current.value + "\n");
        }
      } else if (inputRef.current.value?.trim()) {
        // Enter
        handleButtonSend(event);
      }
    }
  };

  const handlePasterAnchor = isShow => () => setPasterAnchorEl(isShow ? inputRef.current : null);

  const handleChangeInput = event => {
    const inputValue = event.target.value;
    const cursorIndex = getCursorPosition(inputRef.current);
    const previousCharacter = inputValue[cursorIndex - 1];

    const cursorWord = getWordAtCursor(inputValue, cursorIndex);
    const mentionInCurrentWord = getAllLetterIndex(cursorWord, "@");
    const isValidMention = cursorWord.startsWith("@") && mentionInCurrentWord.length === 1;

    if (!inputValue) {
      handleResetInput();
    } else if (cursorIndex > 0 && mentionIdListRef.current.length < mentionList.length && isValidMention) {
      const mentionIndexes = getAllLetterIndex(inputValue, "@");
      const currentIndexes = mentionIndexes.filter(item => item < cursorIndex);

      // Filter member by name
      if (false === [" ", "\n"].includes(previousCharacter) && currentIndexes.length > 0) {
        const nearestIndex = Math.max(...currentIndexes);

        const value2Search = inputValue.slice(nearestIndex + 1, cursorIndex);
        const filteredArray = getFilterArray(mentionList, value2Search).filter(
          item => !mentionIdListRef.current.includes(item.id),
        );

        setFilteredGroupMembers(filteredArray);
        setIsShowTagList(filteredArray.length > 0);
      }
    } else {
      handleCloseTagList();
    }

    const contentMention = replaceName2Id(inputValue, mentionList);
    const mentionArr = mentionList.reduce((results, member) => {
      const mention = "@{" + member.id + "}";
      if (contentMention.includes(mention)) {
        results = [...results, member.id];
      }
      return results;
    }, []);

    mentionIdListRef.current = removeDuplicateInArray(mentionArr);
    updateMessageContent(inputValue);
  };

  const handleCloseTagList = () => setIsShowTagList(false);

  const handleSelectTag = person => {
    const isValid = person && person.id;
    if (!isValid) return;
    mentionIdListRef.current = [...mentionIdListRef.current, person.id];

    // Replace mention name by searching value
    const cursorIndex = getCursorPosition(inputRef.current);
    const preContent = inputRef.current.value.slice(0, cursorIndex);
    const otherContent = inputRef.current.value.slice(cursorIndex);
    const preContentWithMention = replaceByMentionName(preContent, `@${person.name || ""}`);
    const newContent = `${preContentWithMention}${otherContent.startsWith(" ") ? "" : " "}${otherContent}`;
    updateMessageContent(newContent);

    const newCursorIndex = preContentWithMention.length + 1;
    inputRef.current.setSelectionRange(newCursorIndex, newCursorIndex);
    handleCloseTagList();
  };

  const handleButtonSend = event => {
    event.preventDefault();

    let mentionIds = mentionIdListRef.current;
    const mentionAllIndex = mentionIdListRef.current.indexOf(SystemConstant.MENTION_ALL.id);
    if (mentionAllIndex >= 0) {
      const mentionAll = `@{${SystemConstant.MENTION_ALL.id}}`;
      mentionIds[mentionAllIndex] = mentionAll;
    }

    const messageOptions = {};
    if (isSendAsDirectMsg) messageOptions.send_as_direct_message = SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;

    if (quoteMessage) {
      const messageContent = JSON.stringify({
        quote_id: quoteMessage.sourceId,
        content_message: replaceName2Id(inputRef.current.value?.trim(), mentionList),
      });
      onSendMessage(messageContent, mentionIds, messageOptions, SystemConstant.SEND_TYPE.quote);
    } else {
      onSendMessage(replaceName2Id(inputRef.current.value?.trim(), mentionList), mentionIds, messageOptions);
    }

    resetDraftMessage();
    handleResetInput();
    dispatch(
      ConversationActions.conversationSet({
        quoteMessage: null,
        sendAsDirectMsgOpt: SystemConstant.THREAD_MSG_SEND_OPTION.notSendAsDirect,
      }),
    );
  };

  const handlePasteTagMessage = pasteContent => {
    insertContentInput(pasteContent);
    const contentMention = replaceName2Id(pasteContent, mentionList);
    const mentionArr = mentionList.reduce((results, member) => {
      const mention = "@{" + member.id + "}";
      if (contentMention.includes(mention)) {
        results = [...results, member.id];
      }
      return results;
    }, []);

    const currentTagList = mentionIdListRef.current;

    mentionIdListRef.current = removeDuplicateInArray(currentTagList.concat(mentionArr));
  };

  const handleChangeSend2Channel = event => {
    const sendOption = event.target.checked
      ? SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect
      : SystemConstant.THREAD_MSG_SEND_OPTION.notSendAsDirect;
    dispatch(
      ConversationActions.conversationSet({
        sendAsDirectMsgOpt: sendOption,
      }),
    );
  };

  useEffect(() => {
    if (categoryId) {
      handleResetInput();
      const updateStateRedux = { quoteMessage: null };
      if (!isMediaUpload) updateStateRedux.sendAsDirectMsgOpt = SystemConstant.THREAD_MSG_SEND_OPTION.notSendAsDirect;

      dispatch(ConversationActions.conversationSet(updateStateRedux));
    }
  }, [categoryId]);

  useEffect(() => {
    if (
      quoteMessage &&
      quoteMessage.senderId !== accountId &&
      groupDetail &&
      isGroupOrChannelType(groupDetail.groupType) &&
      !mentionIdListRef.current.includes(quoteMessage.senderId)
    ) {
      mentionIdListRef.current = [...mentionIdListRef.current, quoteMessage.senderId];

      const currentContent = inputRef.current.value;
      const preContentWithMention = replaceByMentionName("", `@${quoteMessage.senderName || ""}`);

      const newContent = `${preContentWithMention}${currentContent.startsWith(" ") ? "" : " "}${currentContent}`;
      updateMessageContent(newContent);

      const newCursorIndex = newContent.length;
      inputRef.current.setSelectionRange(newCursorIndex, newCursorIndex);
    }
  }, [quoteMessage]);

  const isAllowAttachment = false === Boolean(quoteMessage && quoteMessage.id) && isShowUploadIcon;

  return (
    <Box className={classes.inputType}>
      {quoteMessage && <Quote quoteMessage={quoteMessage} endIcon={<Close sx={{ fontSize: 20 }} />} />}

      <TextField
        autoFocus
        multiline
        minRows={1}
        maxRows={5}
        onKeyDown={handleKeyDown}
        className={classes.input}
        onChange={handleChangeInput}
        placeholder={placeholder}
        inputRef={inputRef}
        InputProps={{
          onContextMenu: handlePasterAnchor(true),
          classes: {
            notchedOutline: classes.noBorder,
          },
          startAdornment: (
            <CustomInputAdornment position="start">
              <FullEmojiLayout onSelect={insertContentInput} />
            </CustomInputAdornment>
          ),
          endAdornment: (
            <InputEndAdornment
              isShowUploadIcon={isAllowAttachment}
              disabled={quoteMessage && inputRef.current.value?.length === 0}
              onClickSend={handleButtonSend}
            />
          ),
        }}
      />

      {isThreadMode && (
        <FormControlLabel
          onChange={handleChangeSend2Channel}
          control={<Checkbox checked={isSendAsDirectMsg} />}
          label={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_SEND_TO_CHANNEL_OPT")}
          classes={{ root: classes.sendToChannel }}
        />
      )}

      {isShowTagList && (
        <PeopleTagList
          isShow={isShowTagList}
          mentionMembers={filteredGroupMembers}
          onClick={handleSelectTag}
          onClose={handleCloseTagList}
        />
      )}

      <PastePopper anchorEl={pasterAnchorEl} onPaste={handlePasteTagMessage} onClose={handlePasterAnchor(false)} />
    </Box>
  );
};

MessengerChatInput.propTypes = {
  placeholder: PropTypes.string,
  isAllowAttach: PropTypes.bool,

  onSendMessage: PropTypes.func,
  onInputChange: PropTypes.func,
};

MessengerChatInput.defaultProps = {
  placeholder: "",
  isAllowAttach: true,

  onSendMessage: () => {},
  onInputChange: () => {},
};

export default memo(MessengerChatInput);

const CustomInputAdornment = forwardRef((props, ref) => <InputAdornment ref={ref} {...props} />);

const InputEndAdornment = ({ isShowUploadIcon, disabled, onClickSend }) => {
  const classes = useStyles();
  return (
    <CustomInputAdornment position="end">
      <Box>
        {isShowUploadIcon ? (
          <SendMessageFile />
        ) : (
          <IconButton className={classes.sendBtn} disabled={disabled} onClick={onClickSend}>
            <SendInputIcon />
          </IconButton>
        )}
      </Box>
    </CustomInputAdornment>
  );
};

const useStyles = makeStyles(theme => ({
  noBorder: {
    border: "none",
  },

  inputType: {
    width: "100%",
    position: "relative",
    backgroundColor: "#F3F3F5",
    borderRadius: 10,
  },

  input: {
    width: "100%",
    backgroundColor: "#F3F3F5",
    borderRadius: 10,
    zIndex: 10,
  },

  sendBtn: {
    color: theme.palette.primary.main,
    "&:disabled": {
      color: theme.palette.grey[200],
    },
  },

  sendToChannel: {
    padding: "0px 14px",
    margin: 0,
  },
}));
